import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { useNavigate } from "react-router-dom";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";
import useDebounce from "../hooks/useDebouncer.js";
import { useToast } from "../hooks/useToast.js";
import AddNoteModal from "../pages/AddNotes.jsx";
import { getCurrentUser } from "../services/authService.js";
import {
  createBulkLead,
  fetchLeadsForCurrentUser,
} from "../services/leadService.js";
import BulkUploadModal from "./../components/BulkUploadModal";
import BulkAssignModal from "../components/BulkAssignModal.jsx";
import EditLeadModal from "./EditLeadModal.jsx";
import LeadHeader from "../components/leads/LeadHeader.jsx";
import LeadSearch from "../components/leads/LeadSearch.jsx";
import LeadSubHeader from "../components/leads/LeadSubHeader.jsx";
import LeadCardList from "../components/leads/LeadCardList.jsx";
import { fetchUsersByRole } from "../services/teamService.js";

const filterOptions = [
  { label: "New", value: "new" },
  { label: "Pending", value: "pending" },
  { label: "Completed", value: "completed" },
];

const stageMap = {
  call_back: "Call Back",
  meeting: "Meeting Setup",
  quotation_created: "Quotation Created",
  quotation_send: "Quotation Send",
  quotation_revised: "Quotation Revised",
  won: "Won",
  lost: "Lost",
  new: "New",
};

const CACHE_KEY = "leads_cache";
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

function saveCache(data) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ...data, ts: Date.now() }),
    );
  } catch {}
}

function loadCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function pollAndScroll(id, onDone, retries = 20, interval = 50) {
  const poll = (remaining) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "instant", block: "center" });
      onDone?.();
      return;
    }
    if (remaining <= 0) {
      onDone?.();
      return;
    }
    setTimeout(() => poll(remaining - 1), interval);
  };
  poll(retries);
}

export default function Leads() {
  const isAssignFlowRef = useRef(false);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "new";
  const [activeFilter, setActiveFilter] = useState(initialTab);
  const [activeTabLabel, setActiveTabLabel] = useState("New");
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const assignScrollLeadId = useRef(null);

  const [contextMenu, setContextMenu] = useState(null);

  const [pages, setPages] = useState({ new: 1, pending: 1, completed: 1 });
  const [hasMore, setHasMore] = useState({
    new: true,
    pending: true,
    completed: true,
  });
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [error, setError] = useState("");
  const [leads, setLeads] = useState({ new: [], pending: [], completed: [] });

  const [reps, setReps] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const currentUserRef = useRef(null);
  const repsRef = useRef([]);

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteComment, setNoteComment] = useState("");
  const [noteDate, setNoteDate] = useState("");
  const [noteTime, setNoteTime] = useState("");
  const [activeLeadId, setActiveLeadId] = useState(null);
  const [currentStage, setCurrentStage] = useState("");

  const debouncedSearchQuery = useDebounce(searchQuery, 1000);
  const { toast } = useToast();

  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkAssignedTo, setBulkAssignedTo] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkRowCount, setBulkRowCount] = useState(0);
  const fileInputRef = useRef(null);
  const [isFileInvalid, setIsFileInvalid] = useState(true);

  const [tabCounts, setTabCounts] = useState({
    new: null,
    pending: null,
    completed: null,
  });

  // Bulk assign state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [assignReps, setAssignReps] = useState([]);

  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const isRestoringRef = useRef(false);

  const triggerFilePicker = () => fileInputRef.current?.click();

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    repsRef.current = reps;
  }, [reps]);

  const fetchPage = useCallback(
    async (filter, page, isAppend = false) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      if (isAppend) setIsFetchingMore(true);
      else setIsInitialLoading(true);
      setError("");

      try {
        let user = currentUserRef.current;
        let repsList = repsRef.current;

        if (!user) {
          const userRes = await getCurrentUser();
          if (!userRes.success) throw new Error("Failed to get current user");
          user = userRes.user;
          setCurrentUser(user);
          currentUserRef.current = user;
        }

        const leadsData = await fetchLeadsForCurrentUser({
          page,
          status: debouncedSearchQuery.trim() ? "" : filter,
          search: (debouncedSearchQuery || "").replace(/^@/, "").trim(),
          deal_status: filter,
          limit: 10,
        });

        if (!leadsData?.leads) throw new Error("Failed to fetch leads");

        const mappedLeads = leadsData.leads.map((lead) => ({
          ...lead,
          deal_status_alias:
            stageMap[lead.deal_status] || lead.deal_status || "New",

          // ✅ trust backend response
          agent_name: lead.agent_name || "Unassigned",
        }));

        if (typeof leadsData.total === "number") {
          setTabCounts((prev) => ({
            ...prev,
            [filter]: leadsData.total,
          }));
        }

        setLeads((prev) => {
          const next = {
            ...prev,
            [filter]: isAppend
              ? [...prev[filter], ...mappedLeads]
              : mappedLeads,
          };
          saveCache({
            leads: next,
            pages: { ...pages, [filter]: page },
            hasMore: {
              ...hasMore,
              [filter]: page < (leadsData.totalPages || 1),
            },
            activeFilter: filter,
            tabCounts: {
              ...tabCounts,
              [filter]: leadsData.total ?? tabCounts[filter],
            },
          });
          return next;
        });

        setHasMore((prev) => ({
          ...prev,
          [filter]: page < (leadsData.totalPages || 1),
        }));
      } catch (err) {
        setError(err?.message || "Failed to fetch leads");
        if (!isAppend) setLeads((prev) => ({ ...prev, [filter]: [] }));
      } finally {
        if (isAppend) setIsFetchingMore(false);
        else setIsInitialLoading(false);
        isLoadingRef.current = false;
      }
    },
    [debouncedSearchQuery],
  );

  useEffect(() => {
    const close = () => setContextMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  useEffect(() => {
    const scrollData = sessionStorage.getItem("leads_scroll");
    if (!scrollData) return;

    const cache = loadCache();
    if (!cache) return;

    const { leadId } = JSON.parse(scrollData);

    isRestoringRef.current = true;

    setLeads(cache.leads || { new: [], pending: [], completed: [] });
    setPages(cache.pages || { new: 1, pending: 1, completed: 1 });
    setHasMore(cache.hasMore || { new: true, pending: true, completed: true });
    if (cache.tabCounts) setTabCounts(cache.tabCounts);
    if (cache.activeFilter) setActiveFilter(cache.activeFilter);

    pollAndScroll(`lead-${leadId}`, () => {
      sessionStorage.removeItem("leads_scroll");
      isRestoringRef.current = false;
    });
  }, []);

  useEffect(() => {
    if (isRestoringRef.current) return;
    if (debouncedSearchQuery.startsWith("@")) return;
    setPages((prev) => ({ ...prev, [activeFilter]: 1 }));
    setLeads((prev) => ({ ...prev, [activeFilter]: [] }));
    setHasMore((prev) => ({ ...prev, [activeFilter]: true }));
    setSearchParams({ tab: activeFilter });
  }, [activeFilter, debouncedSearchQuery]);

  useEffect(() => {
    if (isRestoringRef.current) return;
    if (debouncedSearchQuery.startsWith("@")) return;
    const page = pages[activeFilter];
    fetchPage(activeFilter, page, page > 1);
  }, [activeFilter, pages[activeFilter], debouncedSearchQuery]);

  const activeFilterRef = useRef(activeFilter);
  const hasMoreRef = useRef(hasMore);
  useEffect(() => {
    activeFilterRef.current = activeFilter;
  }, [activeFilter]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        const filter = activeFilterRef.current;
        if (!hasMoreRef.current[filter]) return;
        if (isLoadingRef.current) return;
        setPages((prev) => ({ ...prev, [filter]: prev[filter] + 1 }));
      },
      { rootMargin: "300px 0px", threshold: 0 },
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);

    return () => observerRef.current?.disconnect();
  }, []);

  const setSentinelRef = useCallback((node) => {
    sentinelRef.current = node;
    if (observerRef.current && node) observerRef.current.observe(node);
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem("assign_scroll_lead_id");
    if (!raw) return;

    const { leadId } = JSON.parse(raw);

    const el = document.getElementById(`lead-${leadId}`);
    if (el) {
      pollAndScroll(`lead-${leadId}`, () => {
        sessionStorage.removeItem("assign_scroll_lead_id");
      });
      return;
    }

    if (hasMore[activeFilter] && !isLoadingRef.current) {
      setPages((prev) => ({
        ...prev,
        [activeFilter]: prev[activeFilter] + 1,
      }));
    }
  }, [leads, hasMore, activeFilter]);

  const loadAllData = useCallback(() => {
    sessionStorage.removeItem(CACHE_KEY);
    isRestoringRef.current = false;

    setPages((prev) => ({ ...prev, [activeFilter]: 1 }));
    setLeads((prev) => ({ ...prev, [activeFilter]: [] }));
    setHasMore((prev) => ({ ...prev, [activeFilter]: true }));
    setTabCounts((prev) => ({ ...prev, [activeFilter]: null }));
  }, [activeFilter]);

  const handleLeadAssigned = useCallback(
    (updatedLead) => {
      if (!updatedLead?.id) {
        loadAllData();
        return;
      }
      setLeads((prev) => {
        const next = {
          new: prev.new.map((l) =>
            l.id === updatedLead.id ? { ...l, ...updatedLead } : l,
          ),
          pending: prev.pending.map((l) =>
            l.id === updatedLead.id ? { ...l, ...updatedLead } : l,
          ),
          completed: prev.completed.map((l) =>
            l.id === updatedLead.id ? { ...l, ...updatedLead } : l,
          ),
        };
        const cache = loadCache();
        if (cache) saveCache({ ...cache, leads: next });
        return next;
      });
    },
    [loadAllData],
  );

  // Scroll back to lead after assign modal closes
  const handleAssignModalClose = useCallback((open) => {
    setIsAssignModalOpen(open);
    if (!open && assignScrollLeadId.current) {
      const id = assignScrollLeadId.current;
      assignScrollLeadId.current = null;
      pollAndScroll(`lead-${id}`);
    }
  }, []);

  const getLatestComment = (lead) => {
    try {
      const comments = Array.isArray(lead.comment)
        ? lead.comment
        : JSON.parse(lead.comment || "[]");
      return comments.length > 0 ? comments[comments.length - 1] : null;
    } catch {
      return null;
    }
  };

  const handleLeadClick = (leadId) => {
    sessionStorage.setItem(
      "leads_scroll",
      JSON.stringify({ scrollY: window.scrollY, tab: activeFilter, leadId }),
    );
    navigate(`/lead/${leadId}`);
  };

  const openBulkModal = () => {
    setBulkFile(null);
    setBulkRowCount(0);
    setBulkAssignedTo("self");
    setIsBulkModalOpen(true);
  };
  const closeBulkModal = () => setIsBulkModalOpen(false);

  const handleBulkFileChange = (e) => {
    setError("");
    const file = e.target.files?.[0];
    const reset = () => {
      e.target.value = "";
      setBulkFile(null);
      setBulkRowCount(0);
    };
    reset();
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast({
        title: "Invalid File",
        description: "Only .csv files are allowed.",
        variant: "destructive",
      });
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Maximum file size is 4MB.",
        variant: "destructive",
      });
      return;
    }
    setBulkFile(file);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const lines = String(evt.target?.result || "")
        .split(/\r?\n/)
        .filter((l) => l.trim());
      setBulkRowCount(Math.max(0, lines.length - 1));
      toast({
        title: "File Ready",
        description: `Detected ${Math.max(0, lines.length - 1)} rows.`,
      });
    };
    reader.readAsText(file);
  };

  const handleBulkContinue = async () => {
    setError("");
    if (!bulkFile) {
      toast({ title: "No file selected", variant: "destructive" });
      return;
    }
    const formData = new FormData();
    formData.append("csv", bulkFile);
    formData.append(
      "assignedTo",
      bulkAssignedTo === "self" ? currentUser?.id : bulkAssignedTo,
    );
    try {
      const result = await createBulkLead(formData);
      if (result.success) {
        toast({
          title: "Bulk upload successful",
          description: "All rows added",
        });
        closeBulkModal();
        loadAllData();
      } else {
        setIsFileInvalid(true);
        const errorMsg =
          result.errors?.[0]?.error || result.error || "Unknown error";
        const lineNumber = result.errors?.[0]?.line;
        toast({
          title: "Bulk upload failed",
          description: lineNumber
            ? `${errorMsg} at line number ${lineNumber}`
            : errorMsg,
          variant: "destructive",
        });
      }
    } catch (err) {
      setIsFileInvalid(true);
      toast({
        title: "Bulk upload failed",
        description: err?.message || "Unknown error",
        variant: "destructive",
      });
    }
  };

  const visibleLeads = leads[activeFilter];

  return (
    <MobileLayout>
      <div>
        <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-4 sm:space-y-6 mx-auto">
          {/* Header */}
          <LeadHeader
            openBulkModal={openBulkModal}
            selectMode={selectMode}
            setSelectMode={setSelectMode}
            selectedCount={selectedLeadIds.length}
            isAdmin={currentUser?.user_role === "business_admin"}
            onBulkAssign={async () => {
              // Load reps for assign modal
              try {
                const res = await fetchUsersByRole("sales_person", { page: 1, limit: 100 });
                setAssignReps(res?.users || res?.data || []);
              } catch { setAssignReps([]); }
              setIsBulkAssignOpen(true);
            }}
            onSelectAll={() => {
              const currentLeads = leads[activeFilter] || [];
              setSelectedLeadIds(currentLeads.map((l) => l.id));
            }}
            onClearSelection={() => setSelectedLeadIds([])}
          />

          {/* Search */}
          <LeadSearch
            setSearchQuery={setSearchQuery}
            searchQuery={searchQuery}
          />

          {/* Filter Tabs */}
          <LeadSubHeader
            filterOptions={filterOptions}
            setActiveFilter={setActiveFilter}
            setActiveTabLabel={setActiveTabLabel}
            activeFilter={activeFilter}
            activeTabLabel={activeTabLabel}
            tabCounts={tabCounts}
          />

          {/* Leads List */}
          <LeadCardList
            isInitialLoading={isInitialLoading}
            error={error}
            visibleLeads={visibleLeads}
            getLatestComment={getLatestComment}
            activeFilter={activeFilter}
            handleLeadClick={handleLeadClick}
            assignScrollLeadId={assignScrollLeadId}
            setSelectedLead={setSelectedLead}
            setIsAssignModalOpen={setIsAssignModalOpen}
            setSentinelRef={setSentinelRef}
            isFetchingMore={isFetchingMore}
            hasMore={hasMore}
            selectMode={selectMode}
            selectedIds={selectedLeadIds}
            onToggleSelect={(id) => {
              setSelectedLeadIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              );
            }}
            onLeadUpdated={() => {
              // Refresh current tab
              setLeads((prev) => ({ ...prev, [activeFilter]: [] }));
              setPages((prev) => ({ ...prev, [activeFilter]: 1 }));
              setHasMore((prev) => ({ ...prev, [activeFilter]: true }));
            }}
          />
        </div>
      </div>

      <AddNoteModal
        isNoteModalOpen={isNoteModalOpen}
        setIsNoteModalOpen={setIsNoteModalOpen}
        noteComment={noteComment}
        setNoteComment={setNoteComment}
        noteDate={noteDate}
        setNoteDate={setNoteDate}
        noteTime={noteTime}
        setNoteTime={setNoteTime}
        saveNote={() => setIsNoteModalOpen(false)}
        leadId={activeLeadId}
        currentStage={currentStage}
        setCurrentStage={setCurrentStage}
        onCommentAdded={loadAllData}
      />

      <BulkUploadModal
        isOpen={isBulkModalOpen}
        closeModal={closeBulkModal}
        bulkAssignedTo={bulkAssignedTo}
        setBulkAssignedTo={setBulkAssignedTo}
        bulkFile={bulkFile}
        bulkRowCount={bulkRowCount}
        handleBulkFileChange={handleBulkFileChange}
        handleBulkContinue={handleBulkContinue}
        triggerFilePicker={triggerFilePicker}
        reps={reps}
        currentUser={currentUser}
        isFileInvalid={isFileInvalid}
        setIsFileInvalid={setIsFileInvalid}
      />

      {isAssignModalOpen && (
        <EditLeadModal
          isOpen={isAssignModalOpen}
          setIsOpen={handleAssignModalClose}
          lead={selectedLead}
          assignOnly={true}
          onLeadUpdated={handleLeadAssigned}
        />
      )}

      <BulkAssignModal
        isOpen={isBulkAssignOpen}
        onClose={() => setIsBulkAssignOpen(false)}
        selectedLeadIds={selectedLeadIds}
        reps={assignReps}
        currentUser={currentUser}
        onAssigned={() => {
          setSelectedLeadIds([]);
          setSelectMode(false);
          // Refresh leads
          setLeads({ new: [], pending: [], completed: [] });
          setPages({ new: 1, pending: 1, completed: 1 });
          setHasMore({ new: true, pending: true, completed: true });
        }}
      />
    </MobileLayout>
  );
}
